import {
  AppConfig,
  AppConstants,
  useSubscriptionAccessConfigContext,
} from '@ltvco/refresh-lib/ctx';
import { useAccountList } from '@ltvco/refresh-lib/hooks';
import { Pagination, Stack, useMediaQuery } from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import {
  DeleteModal,
  generateQueryConfig,
  stringifyParams,
  useBbqSearch,
  useRecentReports,
  type RecentReportSerialized,
  type ReportFilter,
  type ReportType,
  LizanoListInfo,
} from '@ltvco/refresh-lib/v2';
import {
  SmartphoneOutlined,
  PersonOutlined,
  HomeOutlined,
  EmailOutlined,
  DirectionsCarOutlined,
  AlternateEmailOutlined,
} from '@mui/icons-material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryParams } from 'use-query-params';

import {
  ReportCountBar,
  ReportLibraryBreadcrumb,
} from 'components/DeepSearch/components/ReportLibrary/components';
import { EmptyState } from 'components/DeepSearch/components/ReportLibrary/EmptyState';
import {
  filterSortPaginateListViewReports,
  getFilteredListViewReportCount,
  LIST_VIEW_PAGE_SIZE,
  phoneAndFraudScanFilterOptions,
} from 'components/DeepSearch/components/ReportLibrary/Lists/components/ListView/filterListViewReports';
import { ReportLibraryListLayout } from 'components/DeepSearch/components/ReportLibrary/Lists/components/ListView/ReportLibraryListLayout';
import { ReportCardActions } from 'components/DeepSearch/components/ReportLibrary/Lists/components/ReportCardActions';
import {
  ReportListItem,
  ReportListMetadataRow,
} from 'components/DeepSearch/components/ReportLibrary/Lists/components/ReportListItem/index';
import { ViewButton } from 'components/DeepSearch/components/ReportLibrary/MonitoredReports/MonitoredReportsPage.styles';
import {
  handlePageChange,
  handleReportLibraryClick,
  listReportRemovePayloads,
  moreWaysItemsForEmptyState,
  serializeListReports,
} from 'components/DeepSearch/components/ReportLibrary/utils/utils';
import {
  DEEPSEARCH_REPORT_TYPES_MAP,
  DEEPSEARCH_ROUTES,
} from 'components/DeepSearch/constants';
import { ChatLayoutContext } from 'components/DeepSearch/context/ChatLayoutContext';
import emptyListImage from 'images/img_empty_list.png';
import monitoredReportsEmptyImage from 'images/img_monitored_reports_empty.png';
import {
  LoadingReports,
  SearchAndFilterControls,
} from 'routes/Dashboard/RecentReports/components';

const queryConfig = generateQueryConfig();

const reportTypeIcons: Record<string, React.ReactNode> = {
  reverse_phone_report: <SmartphoneOutlined color="primary" fontSize="small" />,
  detailed_person_report: <PersonOutlined color="primary" fontSize="small" />,
  property_report: <HomeOutlined color="primary" fontSize="small" />,
  social_network_report: <EmailOutlined color="primary" fontSize="small" />,
  username_report: <AlternateEmailOutlined color="primary" fontSize="small" />,
  vehicle_report: <DirectionsCarOutlined color="primary" fontSize="small" />,
};

export type ListViewComponentProps = {
  lists: LizanoListInfo[];
  onBack: () => void;
  breadcrumbTitle: string;
  recentReports?: RecentReportSerialized[];
  headerButtonElement?: ReactElement;
  basePath?: string;
};

export function ListViewComponent({
  lists,
  onBack,
  breadcrumbTitle,
  recentReports,
  headerButtonElement,
  basePath = DEEPSEARCH_ROUTES.chat,
}: ListViewComponentProps) {
  const { isMobile } = useScreenSize();
  const { reportState } = useContext(ChatLayoutContext);
  const { openReportFromReportLibrary, openReport, openFilters } = reportState;
  const { logError } = useContext(AppConfig);
  const isMobileBreakpoint = useMediaQuery('(max-width: 1024px)');
  const isColumnOpen = openReport || openFilters;

  const navigate = useNavigate();
  const {
    config: { reportTypeFilterOptions },
  } = useContext(AppConstants);
  const { vinVariation, phoneOnlyVariation } =
    useSubscriptionAccessConfigContext();

  const { searchBy, onSearchChange } = useBbqSearch();
  const [queryParams, setQueryParams] = useQueryParams(queryConfig);
  const { sort_by, sort_direction } = queryParams;

  const [filterTypes, setFilterTypes] = useState<ReportFilter[]>(
    queryParams.report_type || []
  );

  const isRecentView = recentReports !== undefined;
  /** Only Saved/custom account lists support remove-from-list + remove all. */
  const isAccountListView = !isRecentView;

  const recentReportsApiParams = useMemo(() => {
    if (!isRecentView) {
      return {} as Parameters<typeof useRecentReports>[0];
    }
    const baseReportTypes = (queryParams.report_type || []).filter(
      (type) => (type as ReportFilter) !== 'monitored_report'
    );
    const effectiveReportTypesForApi =
      phoneOnlyVariation && baseReportTypes.length === 0
        ? [...phoneAndFraudScanFilterOptions]
        : baseReportTypes;
    return {
      ...stringifyParams(
        {
          ...queryParams,
          report_type: effectiveReportTypesForApi,
        },
        searchBy
      ),
      generator: 'chat',
    };
  }, [isRecentView, phoneOnlyVariation, queryParams, searchBy]);

  const { data: recentQueryData, isLoading: isRecentReportsLoading } =
    useRecentReports(recentReportsApiParams, { enabled: isRecentView });

  const serializedFromProps = useMemo(() => {
    if (isRecentView) {
      return [];
    }
    return serializeListReports(lists);
  }, [isRecentView, lists]);

  const [localReports, setLocalReports] =
    useState<RecentReportSerialized[]>(serializedFromProps);
  const [deleteAllModalOpen, setDeleteAllModalOpen] = useState(false);
  const [isRemovingAllFromList, setIsRemovingAllFromList] = useState(false);
  const { removeReportFromList } = useAccountList();

  useEffect(() => {
    if (isRecentView) {
      return;
    }
    setLocalReports(serializedFromProps);
  }, [isRecentView, serializedFromProps]);

  useEffect(() => {
    if (!isRecentView) {
      return;
    }
    setLocalReports(
      (recentQueryData?.reports as RecentReportSerialized[]) ?? []
    );
  }, [isRecentView, recentQueryData]);

  useEffect(() => {
    setQueryParams({
      page: 1,
      report_type: [],
      sort_by: 'last_viewed_at',
      sort_direction: 'desc',
      permalink: undefined,
      monitored: undefined,
      upgraded: undefined,
      claimed: undefined,
    });
    setFilterTypes([]);
  }, []);

  const reportTypes: ReportType[] = filterTypes.filter(
    (type) => type !== 'monitored_report'
  );
  const effectiveReportTypesForSync =
    phoneOnlyVariation && reportTypes.length === 0
      ? [...phoneAndFraudScanFilterOptions]
      : reportTypes;

  const hasZeroReports = isRecentView
    ? (recentQueryData?.meta?.report_quantities?.total_reports_count ?? 0) === 0
    : serializedFromProps.length === 0;

  useEffect(() => {
    if (phoneOnlyVariation && filterTypes.length === 0) {
      setFilterTypes([...phoneAndFraudScanFilterOptions] as ReportFilter[]);
    }
  }, [phoneOnlyVariation, filterTypes.length]);

  useEffect(() => {
    setQueryParams({
      report_type: effectiveReportTypesForSync,
      page: 1,
    });
  }, [
    setQueryParams,
    filterTypes,
    hasZeroReports,
    searchBy,
    queryParams.sort_by,
    queryParams.sort_direction,
    phoneOnlyVariation,
  ]);

  const availableFilterTypeOptions = { ...reportTypeFilterOptions };

  const currentDeepsearchReportTypes = Object.values(
    DEEPSEARCH_REPORT_TYPES_MAP
  );

  const filteredReportTypeOptions = Object.keys(availableFilterTypeOptions)
    .filter((key) => currentDeepsearchReportTypes.includes(key as ReportType))
    .reduce((acc, key) => {
      acc[key] = availableFilterTypeOptions[key];
      return acc;
    }, {} as typeof availableFilterTypeOptions);

  const getReportTypeFilterOptions = () => {
    if (vinVariation && filteredReportTypeOptions.vehicle_report) {
      return { vehicle_report: filteredReportTypeOptions.vehicle_report };
    }
    if (phoneOnlyVariation) {
      return Object.keys(filteredReportTypeOptions)
        .filter((key) =>
          (phoneAndFraudScanFilterOptions as readonly string[]).includes(key)
        )
        .reduce((acc, key) => {
          acc[key] = filteredReportTypeOptions[key];
          return acc;
        }, {} as typeof filteredReportTypeOptions);
    }
    return filteredReportTypeOptions;
  };

  const totalFiltered = isRecentView
    ? recentQueryData?.meta?.report_quantities?.filtered_reports_count ?? 0
    : getFilteredListViewReportCount(
        localReports,
        queryParams,
        phoneOnlyVariation,
        searchBy
      );

  const pageCount = Math.ceil(totalFiltered / LIST_VIEW_PAGE_SIZE);

  const displayedReports = useMemo(() => {
    if (isRecentView) {
      return localReports;
    }
    return filterSortPaginateListViewReports(
      localReports,
      queryParams,
      phoneOnlyVariation,
      searchBy
    );
  }, [isRecentView, localReports, queryParams, phoneOnlyVariation, searchBy]);

  const showRecentEmpty =
    isRecentView &&
    !isRecentReportsLoading &&
    (recentQueryData?.meta?.report_quantities?.filtered_reports_count ?? 0) ===
      0;

  const isAccountListWithNoReports =
    !isRecentView && serializedFromProps.length === 0;

  const showListEmptyState = showRecentEmpty || isAccountListWithNoReports;

  const emptyStateImage =
    breadcrumbTitle === 'Monitored Reports'
      ? monitoredReportsEmptyImage
      : emptyListImage;

  const deleteCurrentReport = (permalink: string) => {
    setLocalReports((prev) => prev.filter((report) => report.id !== permalink));
  };

  const handleDeleteAllConfirm = async (
    event: MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    if (!isAccountListView) {
      setDeleteAllModalOpen(false);
      return;
    }
    const visiblePermalinks = new Set(localReports.map((report) => report.id));
    const payloads = listReportRemovePayloads(lists).filter(
      ({ reportPermalink }) => visiblePermalinks.has(reportPermalink)
    );
    if (payloads.length === 0) {
      setDeleteAllModalOpen(false);
      return;
    }
    setIsRemovingAllFromList(true);
    try {
      for (const { reportPermalink, ...mutationPayload } of payloads) {
        await removeReportFromList.mutateAsync(mutationPayload);
        setLocalReports((prev) => prev.filter((r) => r.id !== reportPermalink));
      }
      setLocalReports([]);
      setDeleteAllModalOpen(false);
    } catch (error) {
      logError(
        'Error removing all reports from list',
        error instanceof Error
          ? error
          : new Error('Unknown error removing reports')
      );
      setDeleteAllModalOpen(false);
    } finally {
      setIsRemovingAllFromList(false);
    }
  };

  const handleDeleteAllModalClose = (event?: MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    event?.preventDefault();
    setDeleteAllModalOpen(false);
  };

  const searchAndFilterControls = (
    <SearchAndFilterControls
      filterTypes={filterTypes}
      setFilterTypes={setFilterTypes}
      onSearchChange={onSearchChange}
      setQueryParams={setQueryParams}
      reportTypeFilterOptions={getReportTypeFilterOptions()}
      sort_by={sort_by}
      sort_direction={sort_direction}
      isInChatPage
      chatSearchWidth={openReport && !isMobile ? '250px' : undefined}
    />
  );

  const listBody = (
    <>
      <ReportLibraryBreadcrumb title={breadcrumbTitle} onBack={onBack} />
      <ReportLibraryListLayout headerActions={headerButtonElement}>
        {isRecentReportsLoading ? (
          <LoadingReports />
        ) : showListEmptyState ? (
          <>
            {searchAndFilterControls}
            <EmptyState
              actionIcon={<AutoAwesomeIcon />}
              image={emptyStateImage}
              title={
                isRecentView
                  ? 'No reports found.'
                  : 'No reports in this list yet.'
              }
              description={
                isRecentView
                  ? 'Try adjusting your filters or search.'
                  : 'Reports you add from search will appear here.'
              }
              actionLabel="New Chat"
              onAction={() => navigate(basePath)}
              items={moreWaysItemsForEmptyState(navigate, basePath)}
            />
          </>
        ) : (
          <>
            {searchAndFilterControls}
            <ReportCountBar
              count={displayedReports.length}
              showDeleteAll={isAccountListView}
              disableDeleteAll={isRemovingAllFromList}
              onDeleteAll={() => setDeleteAllModalOpen(true)}
            />
          </>
        )}
      </ReportLibraryListLayout>
      {!isRecentReportsLoading && !showListEmptyState && (
        <Stack
          spacing={3}
          sx={{
            flex: 1,
            overflowY: 'auto',
            minHeight: 0,
            width: '100%',
            mt: 2,
          }}
        >
          <Stack spacing={2}>
            {displayedReports.map((report) => {
              const icon = reportTypeIcons[report.reportType] ?? (
                <PersonOutlined color="primary" fontSize="small" />
              );
              return (
                <ReportListItem
                  key={report.id}
                  icon={icon}
                  title={report.reportData}
                  subtitle={`${report.reportTypeFormatted} Report`}
                  metadata={
                    <ReportListMetadataRow
                      updated={report.updatedAt}
                      created={report.createdAt}
                      viewed={report.updatedAt}
                    />
                  }
                  footer={
                    <ReportCardActions
                      permalink={report.id}
                      reportType={report.reportType}
                      reportTitle={report.reportData}
                      deleteCurrentReport={deleteCurrentReport}
                      report={report}
                    />
                  }
                  trailing={
                    <ViewButton
                      variant="text"
                      size="small"
                      onClick={() =>
                        handleReportLibraryClick(
                          report.reportType,
                          report.id,
                          logError,
                          openReportFromReportLibrary
                        )
                      }
                    >
                      View
                    </ViewButton>
                  }
                  onCardClick={() =>
                    handleReportLibraryClick(
                      report.reportType,
                      report.id,
                      logError,
                      openReportFromReportLibrary
                    )
                  }
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgb(248, 248, 248)',
                      cursor: 'pointer',
                    },
                  }}
                />
              );
            })}
          </Stack>
          {pageCount > 1 && (
            <Stack
              spacing={2}
              direction="row"
              sx={{ justifyContent: 'center' }}
            >
              <Pagination
                page={queryParams.page ?? 1}
                count={pageCount}
                onChange={(_: unknown, page: number) =>
                  handlePageChange(page, setQueryParams)
                }
                variant="outlined"
                shape="rounded"
                sx={{
                  '& .MuiPaginationItem-page, & .MuiPaginationItem-ellipsis': {
                    display: 'none',
                  },
                }}
              />
            </Stack>
          )}
          {isAccountListView && (
            <DeleteModal
              isOpen={deleteAllModalOpen}
              setIsOpen={handleDeleteAllModalClose}
              deleteMutation={handleDeleteAllConfirm}
              title="Remove all reports from this list?"
              confirmationText="Reports will be removed from this list only. They will still be available from search or recent reports."
              deleteText="Remove all"
            />
          )}
        </Stack>
      )}
    </>
  );

  return (
    <Stack
      width="100%"
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        ...(isMobileBreakpoint &&
          isColumnOpen && {
            display: 'none',
          }),
      }}
    >
      {listBody}
    </Stack>
  );
}
